import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftRight,
  ArrowRight,
  BadgeDollarSign,
  Gift,
  LineChart,
  ShieldCheck,
  Sparkles,
  Tag,
  Trophy,
  Users,
  Wallet,
  Zap,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import apiService from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import { formatPts } from '../../utils/moduleHelpers';
import '../../styles/module-shared.css';
import './rewards-module.css';

const STEPS = [
  {
    title: 'Earn',
    body: 'Collect PTS from trading volume, deposits, promotions, and milestone unlocks.',
    Icon: Trophy,
  },
  {
    title: 'Choose',
    body: 'Browse the rewards catalog and pick a prize that matches your available points.',
    Icon: Gift,
  },
  {
    title: 'Claim',
    body: 'Confirm your selection and redeem instantly — track everything in Rewards Report.',
    Icon: Wallet,
  },
];

/** Display meta for AffiliateLoyaltyEvents (icons / copy / tone). */
const EVENT_META = {
  trading: {
    title: 'Trading',
    body: 'Earn points as your clients generate volume.',
    subtitle: 'Per Standard Lot',
    tone: 'teal',
    Icon: LineChart,
  },
  deposits: {
    title: 'Deposits',
    body: 'Boost PTS when referred clients fund accounts.',
    subtitle: 'Per $100',
    tone: 'emerald',
    Icon: Wallet,
  },
  referrals: {
    title: 'Referrals',
    body: 'Earn PTS for every successful partner referral.',
    subtitle: 'Per Successful Referral',
    tone: 'sky',
    Icon: Users,
  },
  promotions: {
    title: 'Promotions',
    body: 'Seasonal campaigns can grant bonus points.',
    subtitle: 'Limited-time offers',
    tone: 'amber',
    Icon: Tag,
  },
  createdaccount: {
    title: 'Account Verification',
    body: 'One-time bonus when account verification is completed.',
    subtitle: 'One-time bonus',
    tone: 'orange',
    Icon: ShieldCheck,
  },
  transfer: {
    title: 'Transfer',
    body: 'Points credited or moved through transfer activity.',
    subtitle: 'Points transfer activity',
    tone: 'indigo',
    Icon: ArrowLeftRight,
    varies: true,
  },
};

const TONES = ['teal', 'emerald', 'sky', 'amber', 'orange', 'indigo'];

const normalizeKey = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const formatEventTitle = (name) => {
  const raw = String(name || '').trim();
  if (!raw) return 'Earn method';
  return raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const resolveMeta = (event, index) => {
  const key = normalizeKey(event.name);
  if (EVENT_META[key]) return EVENT_META[key];
  if (key.includes('promot')) return EVENT_META.promotions;
  if (key.includes('creat') || key.includes('verif') || key.includes('account')) {
    return EVENT_META.createdaccount;
  }
  if (key.includes('trad')) return EVENT_META.trading;
  if (key.includes('deposit')) return EVENT_META.deposits;
  if (key.includes('refer')) return EVENT_META.referrals;
  if (key.includes('transfer')) return EVENT_META.transfer;
  return {
    title: formatEventTitle(event.name),
    body: event.description || 'Earn loyalty points from this activity.',
    subtitle: event.description || 'Loyalty event',
    tone: TONES[index % TONES.length],
    Icon: Sparkles,
  };
};

const RewardsHowItWorks = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);

  const fetchData = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getLoyaltyEventsList();
      if (cancelled()) return;
      if (!res.success) {
        setError(res.error || 'Failed to load earn events');
        setEvents([]);
        return;
      }
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (!cancelled()) setError(err.message || 'Failed to load earn events');
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey('rewards-how-loyalty-events', fetchData, true);

  const ways = useMemo(
    () =>
      events.map((event, index) => {
        const meta = resolveMeta(event, index);
        const pts = Number(event.points ?? 0);
        const pointsLabel = meta.varies || pts === 0 ? 'Varies' : `${formatPts(pts)} PTS`;
        return {
          id: event.id || index,
          title: formatEventTitle(event.name) || meta.title,
          body: event.description || meta.body || meta.subtitle,
          tone: meta.tone,
          Icon: meta.Icon,
          pointsLabel,
        };
      }),
    [events]
  );

  return (
    <div className="page rhw-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={Gift}
          title="How It Works"
          subtitle="A simple path from earning points to claiming rewards"
          iconBg="linear-gradient(135deg,#6b0000,#8b0000)"
        />
        <ModuleAlert message={error} />

        <section className="rhw-hero">
          <div className="rhw-hero__copy">
            <span className="rhw-eyebrow">
              <Zap className="w-3.5 h-3.5" />
              Simple 3-step flow
            </span>
            <h2>Earn PTS, pick a prize, claim with confidence</h2>
            <p>
              Rewards are designed for partners who grow with us — transparent points, clear
              milestones, and a catalog you can redeem anytime.
            </p>
          </div>
          <div className="rhw-hero__visual" aria-hidden="true">
            <div className="rhw-hero__orb rhw-hero__orb--1" />
            <div className="rhw-hero__orb rhw-hero__orb--2" />
            <div className="rhw-hero__badge">
              <Gift className="w-6 h-6" />
              <strong>3 steps</strong>
              <span>to redeem</span>
            </div>
          </div>
        </section>

        <section className="rhw-steps" aria-label="Reward steps">
          {STEPS.map((step, i) => {
            const Icon = step.Icon;
            return (
              <article key={step.title} className="rhw-step">
                <div className="rhw-step__top">
                  <span className="rhw-step__num">{i + 1}</span>
                  <span className="rhw-step__icon">
                    <Icon className="w-4 h-4" strokeWidth={2} />
                  </span>
                </div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
                {i < STEPS.length - 1 ? (
                  <span className="rhw-step__connector" aria-hidden="true" />
                ) : null}
              </article>
            );
          })}
        </section>

        <section className="rhw-earn">
          <div className="rhw-earn__head">
            <h3>Ways to earn</h3>
            <p>Based on Partner Loyalty Events — every way you can collect PTS.</p>
          </div>

          {loading ? (
            <div className="module-card" style={{ padding: '1.5rem' }}>
              <AppLoader color="#8b0000" />
            </div>
          ) : ways.length === 0 ? (
            <div className="module-card" style={{ padding: '1.25rem', textAlign: 'center' }}>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                No loyalty events available yet.
              </p>
            </div>
          ) : (
            <div className={`rhw-earn-grid${ways.length > 4 ? ' is-wide' : ''}`}>
              {ways.map((item) => {
                const Icon = item.Icon;
                return (
                  <article key={item.id} className={`rhw-earn-item tone-${item.tone}`}>
                    <div className="rhw-earn-item__top">
                      <div className="rhw-earn-item__icon">
                        <Icon className="w-5 h-5" strokeWidth={1.85} />
                      </div>
                      <span className="rhw-earn-item__pts">{item.pointsLabel}</span>
                    </div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <aside className="rhw-tip">
          <div className="rhw-tip__icon">
            <BadgeDollarSign className="w-5 h-5" />
          </div>
          <div>
            <strong>Tip</strong>
            <p>
              Keep an eye on milestone progress on your Rewards Dashboard — unlocking a tier often
              opens higher-value catalog items.
            </p>
          </div>
        </aside>

        <div className="rhw-ctas">
          <Link className="rhw-btn rhw-btn--primary" to="/rewards/claim">
            Browse catalog
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link className="rhw-btn rhw-btn--ghost" to="/rewards/dashboard">
            Back to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RewardsHowItWorks;
